import React from "react";
import { Headset, Medal, Trophy } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { AgentAvatar } from "./AgentAvatar";
import { formatCompact, formatUsd, toNumber } from "./format";
import { cn } from "../../lib/utils";

const RANK_TONES = {
  1: {
    rowBg: "bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent",
    rankBg: "bg-gradient-to-br from-amber-300 to-amber-500",
    rankText: "text-amber-900",
    Icon: Trophy,
    label: "Champion",
  },
  2: {
    rowBg: "bg-gradient-to-r from-slate-400/8 via-transparent to-transparent",
    rankBg: "bg-gradient-to-br from-slate-200 to-slate-400",
    rankText: "text-slate-800",
    Icon: Medal,
    label: "Silver",
  },
  3: {
    rowBg: "bg-gradient-to-r from-orange-500/8 via-transparent to-transparent",
    rankBg: "bg-gradient-to-br from-orange-300 to-orange-600",
    rankText: "text-orange-900",
    Icon: Medal,
    label: "Bronze",
  },
};

const DEFAULT_TONE = {
  rowBg: "",
  rankBg: "bg-white/5 border border-white/10",
  rankText: "text-gray-300",
  Icon: null,
  label: null,
};

function RankBadge({ rank }) {
  const tone = RANK_TONES[rank] || DEFAULT_TONE;
  const Icon = tone.Icon;
  return (
    <div
      className={cn(
        "inline-flex h-9 w-9 items-center justify-center rounded-lg",
        tone.rankBg
      )}
    >
      {Icon ? (
        <Icon className={cn("h-4 w-4", tone.rankText)} />
      ) : (
        <span className={cn("text-xs font-bold tabular-nums", tone.rankText)}>#{rank}</span>
      )}
    </div>
  );
}

function StatusPill({ isActive }) {
  if (isActive == null) {
    return <span className="text-xs text-gray-500">—</span>;
  }
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium",
        isActive
          ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-300"
          : "border-gray-500/30 bg-gray-500/10 text-gray-400"
      )}
    >
      <span
        className={cn(
          "size-1.5 rounded-full",
          isActive ? "bg-emerald-400" : "bg-gray-500"
        )}
      />
      {isActive ? "Active" : "Inactive"}
    </span>
  );
}

function ScoreRow({ rank, agent, totalCalls, meta }) {
  const tone = RANK_TONES[rank] || DEFAULT_TONE;
  const name = agent?.name || "—";
  const calls = toNumber(agent?.calls);
  const share = totalCalls > 0 ? (calls / totalCalls) * 100 : 0;
  const tokens = toNumber(agent?.total_tokens);
  const m = meta || {};

  return (
    <tr
      className={cn(
        "border-t border-white/5 transition-colors hover:bg-white/[0.03]",
        tone.rowBg
      )}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <RankBadge rank={rank} />
          {tone.label && (
            <span className="hidden text-[10px] font-semibold uppercase tracking-wider text-gray-400 lg:inline">
              {tone.label}
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <AgentAvatar name={name} src={m.avatar} size={36} />
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-gray-100">{name}</p>
            {m.type && (
              <p className="truncate text-[11px] uppercase tracking-wider text-gray-500">
                {m.type}
              </p>
            )}
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-right">
        <span className="text-sm font-semibold tabular-nums text-white">
          {formatCompact(calls)}
        </span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-3">
          <span className="w-12 text-right text-xs tabular-nums text-gray-300">
            {share.toFixed(1)}%
          </span>
          <div className="hidden h-1.5 w-24 overflow-hidden rounded-full bg-white/5 md:block">
            <div
              className="h-full rounded-full bg-gradient-to-r from-violet-500 to-pink-500"
              style={{ width: `${Math.max(2, share)}%` }}
            />
          </div>
        </div>
      </td>
      {/* Tokens and cost come from llm_call_logs and are incomplete for models that
          report no usage — a dash means "not reported", not "zero". The card header
          carries the coverage caveat; repeating it per row would drown the table. */}
      <td className="px-4 py-3 text-right text-sm tabular-nums text-gray-300">
        {tokens > 0 ? formatCompact(tokens) : <span className="text-gray-600">—</span>}
      </td>
      <td className="px-4 py-3 text-right text-sm tabular-nums text-gray-300">
        {toNumber(agent?.estimated_cost_usd) > 0 ? (
          formatUsd(agent.estimated_cost_usd)
        ) : (
          <span className="text-gray-600">—</span>
        )}
      </td>
      <td className="px-4 py-3">
        <StatusPill isActive={m.isActive} />
      </td>
    </tr>
  );
}

/**
 * Top agents, ranked by real LLM calls.
 *
 * `data` is the backend's `agents.top_agents`: calls come from `llm_call_logs`, not
 * from counting `chat_message` rows with role='user' — which is what the old
 * dashboard did and called it "calls".
 *
 * Keyed by `assistant_id`, not by name: two agents can share a name, and the old
 * board keyed on the name and would have collapsed them into one row.
 */
export function AgentScoreboard({ data = [], meta = {} }) {
  const ranked = (Array.isArray(data) ? data : [])
    .slice()
    .sort((a, b) => toNumber(b?.calls) - toNumber(a?.calls))
    .filter((a) => a?.assistant_id);

  const total = ranked.reduce((sum, a) => sum + toNumber(a.calls), 0);
  const hasData = ranked.length > 0 && total > 0;

  return (
    <Card className="overflow-hidden rounded-2xl border border-white/10 bg-[#0a0a0a]/85 shadow-[0_16px_45px_rgba(0,0,0,0.4)]">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-gray-100">
              <Trophy className="h-5 w-5 text-amber-300" />
              Top agents leaderboard
            </CardTitle>
            <CardDescription className="text-gray-300">
              Ranked by LLM calls. Tokens and cost are blank where the model reported
              no usage.
            </CardDescription>
          </div>
          <div className="hidden items-center gap-2 rounded-full border border-amber-300/30 bg-amber-500/10 px-3 py-1 text-xs font-medium text-amber-200 md:inline-flex">
            <Trophy className="h-3.5 w-3.5" />
            {ranked.length} contender{ranked.length === 1 ? "" : "s"}
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {hasData ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-t border-white/5 bg-white/[0.02]">
                  <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                    Rank
                  </th>
                  <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                    Agent
                  </th>
                  <th className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                    Calls
                  </th>
                  <th className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                    Share
                  </th>
                  <th className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                    Tokens
                  </th>
                  <th className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                    Cost
                  </th>
                  <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {ranked.map((agent, index) => (
                  <ScoreRow
                    key={agent.assistant_id}
                    rank={index + 1}
                    agent={agent}
                    totalCalls={total}
                    meta={meta[agent.name]}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex h-48 flex-col items-center justify-center gap-2 text-center">
            <Headset className="h-8 w-8 text-purple-300/80" />
            <p className="text-sm font-medium text-gray-200">No agents on the board yet</p>
            <p className="max-w-xs text-xs text-gray-400">
              Once agents start receiving calls, they will appear here ranked by activity.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default AgentScoreboard;
